/**
 * A list of places — the shape the workspaces, the teams and the members all
 * take.
 *
 * Not a card and not a table: the rows run the full width of the page, divided
 * by the faintest rule the palette has, and the whole row is the target. A card
 * around a list draws a box around something that is already bounded by the
 * page. Since the redesign (§40.7) a list may name its columns in a mono
 * header — the Workspaces and Teams tables do.
 *
 * There are no counters here either. A row says what it is and, in its last
 * column before the chevron, the one fact that differs between the person
 * looking: who published it, for somebody who can publish; what gives you
 * access, for somebody who was given it.
 */
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { ChevronDown } from './icons';

export function List({
  children,
  header,
  columns
}: {
  children: React.ReactNode;
  /** The column names (§40.7), on the rows' grid — the chevron's column is added. */
  header?: string[];
  columns?: string;
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {header && columns && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `${columns} 40px`,
            gap: 18,
            alignItems: 'center',
            minHeight: 36,
            padding: '0 var(--al-gutter)',
            boxShadow: `inset 0 -1px 0 ${color.lineStrong}`,
            fontFamily: font.mono,
            fontSize: 10.5,
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            color: color.inkFaint
          }}
        >
          {header.map((cell) => (
            <span key={cell}>{cell}</span>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * A table row that leads to a page (§40.7): the whole row is clickable (hover
 * .035, the hand), the keyboard target is the name — an `<a>` with the page's
 * own address, from `ListName` — not the row, and a chevron turned −90°, 16
 * #6E6E73, sits decorative in a last column of 40.
 */
export function ListRow({
  columns,
  children,
  onOpen
}: {
  /** The grid, without the trailing chevron column — this adds it. */
  columns: string;
  children: React.ReactNode;
  onOpen?: () => void;
}): React.JSX.Element {
  return (
    <div
      className={onOpen ? 'al-link-row' : undefined}
      onClick={
        onOpen
          ? (event) => {
              // The name's link and any control in the row answer for
              // themselves; a click anywhere else on the row opens it.
              if ((event.target as HTMLElement).closest('a, button, input, select')) return;
              onOpen();
            }
          : undefined
      }
      style={{
        display: 'grid',
        alignItems: 'center',
        gap: 18,
        padding: '14px var(--al-gutter)',
        gridTemplateColumns: `${columns} 40px`,
        boxShadow: `inset 0 -1px 0 ${color.hairSoft}`,
        cursor: onOpen ? 'pointer' : 'default'
      }}
    >
      {children}
      <span aria-hidden="true" style={{ display: 'flex', justifyContent: 'flex-end', color: color.inkSubtle }}>
        {onOpen ? (
          <span style={{ display: 'flex', transform: 'rotate(-90deg)' }}>
            <ChevronDown size={16} />
          </span>
        ) : null}
      </span>
    </div>
  );
}

const NAME: React.CSSProperties = {
  minWidth: 0,
  fontSize: 14,
  fontWeight: 500,
  color: color.ink,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

/** The name cell: the colour the workspace carries, then the name. */
export function ListName({
  name,
  swatch,
  href,
  onOpen,
  children
}: {
  name: string;
  /** The page the row leads to: the name becomes its link, the row's keyboard target (§40.7). */
  href?: string;
  onOpen?: () => void;
  /** The document's own colour, where the thing has one. */
  swatch?: string;
  /** Anything that qualifies the name — a flag, a tag. */
  children?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 9 }}>
      {swatch && (
        <span aria-hidden="true" style={{ flex: 'none', width: 9, height: 9, borderRadius: 3, background: swatch }} />
      )}
      {href ? (
        <a
          href={href}
          className="al-row-link"
          onClick={(event) => {
            // A plain click stays in the page; a modified one opens a tab.
            if (!onOpen || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            onOpen();
          }}
          style={{ ...NAME, textDecoration: 'none' }}
        >
          {name}
        </a>
      ) : (
        <span style={NAME}>{name}</span>
      )}
      {children}
    </div>
  );
}

export function ListMeta({ children, mono }: { children: React.ReactNode; mono?: boolean }): React.JSX.Element {
  return (
    <span
      style={{
        minWidth: 0,
        fontFamily: mono ? font.mono : undefined,
        fontSize: mono ? 11.5 : 12.5,
        color: mono ? color.inkFaint : color.inkSubtle,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </span>
  );
}

/**
 * The only alarm a list carries: something named here that is not connected.
 * It is a dot and not a badge, because the row it sits on is still usable —
 * what it marks is a thing to do, not a thing that failed.
 */
export function ListFlag({ title }: { title: string }): React.JSX.Element {
  return (
    <span
      title={title}
      style={{ flex: 'none', width: 6, height: 6, borderRadius: radius.chip, background: color.yellow }}
    />
  );
}

/** A word the row carries rather than says — `AUTO`, `OWNER`, `ARCHIVED`. */
export function ListTag({ children, title }: { children: React.ReactNode; title?: string }): React.JSX.Element {
  return (
    <span
      title={title}
      style={{
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        // A kind label (§8): mono 10.5 capitals +.16em, 20 high, radius 6.
        height: 20,
        padding: '0 7px',
        borderRadius: 6,
        background: color.surfaceAlt,
        fontFamily: font.mono,
        fontSize: 10.5,
        color: color.inkMuted,
        letterSpacing: '.16em'
      }}
    >
      {children}
    </span>
  );
}

/**
 * What a page says when its list is empty. It names what to do next — never
 * "nothing here", which tells somebody they have arrived somewhere useless.
 */
export function ListEmpty({ title, children }: { title: string; children?: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ padding: '40px var(--al-gutter)', maxWidth: 560 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: color.ink }}>{title}</div>
      <div style={{ fontSize: 13, color: color.inkSubtle, marginTop: 7, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

/**
 * A note: a tint, a 2px rail, **four sharp corners**, and prose in the colour
 * of what it says. The sharp corners are the whole point — a rounded tinted
 * box reads as a card, and this is an aside on the page, not a thing on it.
 */
export function Note({
  tone = 'plain',
  children
}: {
  tone?: 'plain' | 'warn' | 'teal';
  children: React.ReactNode;
}): React.JSX.Element {
  // No side rail and no dot since the redesign (§32, owner's call in lot 3B):
  // the ground and the ink carry the tone, the words carry the meaning.
  const [background, ink] =
    tone === 'warn'
      ? [color.warnBg, color.warnText]
      : tone === 'teal'
        ? [color.okBg, color.okText]
        : [color.surfaceAlt, color.inkMuted];
  return (
    <div style={{ padding: '10px 14px', fontSize: 13, lineHeight: 1.5, background, borderRadius: radius.md, color: ink }}>
      {children}
    </div>
  );
}

/** The page's own body: the padding every non-list page shares. */
export function PageBody({
  children,
  gap = 18,
  max
}: {
  children: React.ReactNode;
  gap?: number;
  max?: number;
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap, padding: '20px var(--al-gutter) 30px', maxWidth: max }}>{children}</div>
  );
}

/** The sentence a page opens with, where it needs one. */
export function PageLede({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: color.inkSubtle, maxWidth: 640 }}>{children}</p>;
}

/**
 * A page's own verbs, at the top of the page they act on.
 *
 * This is where the header's slot went. A verb that adds something — a
 * workspace, a team, an invitation, a row of access — now sits above the list
 * it adds to, on the same surface, so what it will do is read off what is
 * underneath it rather than guessed from a bar shared by every page. The bar
 * takes filters too, for the same reason: narrowing a list is an act on that
 * list, and the chrome never knew which list was on screen.
 */
export function PageBar({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        padding: '16px var(--al-gutter) 0'
      }}
    >
      {children}
    </div>
  );
}
